import { IReviewSchema } from '../../../repositories/dailyrent/entities/response/ReviewSchema';
import { EType } from '../../../repositories/newbuilding-reviews/entities/common/UserSchema';
import { EStatus } from '../../../repositories/newbuilding-reviews/entities/common/LikeSchema';
import { IReplySchema } from '../../../repositories/dailyrent/entities/response/ReplySchema';
import { IAnswerSchema } from '../../../repositories/newbuilding-reviews/entities/common/AnswerSchema';
import { EReplyActionType } from '../../../repositories/dailyrent/entities/response/ReplyActionSchema';
import { IExtendedReviewWebSchema, TMappedDailyrentResponse } from '../../../types/reviews';
import { IPublicWebV1GetReviewsResponseSchema } from '../../../repositories/dailyrent/entities/response/PublicWebV1GetReviewsResponseSchema';
import { IObjectInfo } from '../../../types/objectInfo';
import { extractNumber } from '../../../utils/extractNumber';

const extractObjectTitle = (str: string): string => str.replace('Отзывы о ', '');

const mapAnswers = (dailyrentReply?: IReplySchema | null): IAnswerSchema[] => {
  if (!dailyrentReply) {
    return [];
  }

  const answer: IAnswerSchema = {
    comment: {
      text: dailyrentReply.text,
      type: '',
    },
    date: dailyrentReply.createdAt,
    id: String(dailyrentReply.replyId),
    user: {
      id: -1,
      name: dailyrentReply.title,
      avatarUrl: dailyrentReply.avatarUrl,
      type: EType.Regular,
    },
    isAnonymous: false,
    isHidden: true,
    like: {
      status: EStatus.Unknown,
      likesCount: 0,
      dislikesCount: 0,
    },
  };

  return [answer];
};

const mapDailyrentReview = (dailyrentReview: IReviewSchema): IExtendedReviewWebSchema => {
  const hasReply = !!dailyrentReview.reply;

  return {
    id: dailyrentReview.reviewId.toString(),
    user: {
      id: -1,
      name: dailyrentReview.userName,
      avatarUrl: dailyrentReview.avatarUrl,
      type: EType.Regular,
    },
    rating: Number(dailyrentReview.overallScore.replace(',', '.')),
    date: dailyrentReview.createdAt,
    answersCountInt: Number(hasReply),
    answersCount: hasReply ? '1 ответ' : '',
    hasAnswers: hasReply,
    hasMoreAnswers: false,
    isUseful: false,
    isOnModeration: false,
    isLegacy: false,
    like: {
      status: EStatus.Unknown,
      likesCount: 0,
      dislikesCount: 0,
    },
    comment: {
      /** Это поле отображается в шапке отзыва */
      type: dailyrentReview.title,
      positive: dailyrentReview.positiveReview,
      negative: dailyrentReview.negativeReview,
    },
    labels: [],
    isAnonymous: false,
    answers: mapAnswers(dailyrentReview.reply),
    showAnswerButton: dailyrentReview.replyAction?.replyActionType === EReplyActionType.Comment,
  };
};

const mapObjectInfo = (dailyrentData: IPublicWebV1GetReviewsResponseSchema, offerRealtyId: number): IObjectInfo => ({
  fromDeveloperPropsCount: 0,
  objectId: offerRealtyId,
  objectName: extractObjectTitle(dailyrentData.seoData.pageTitle.replace(/(\d+)\.(\d+)/g, '$1,$2')),
  formattedObjectName: extractObjectTitle(dailyrentData.seoData.pageTitle.replace(/(\d+)\.(\d+)/g, '$1,$2')),
  reviewDescription: [''],
  objectUrl: dailyrentData.offerUrl,
  reviewsCount: extractNumber(dailyrentData.rating?.reviewsCount),
  reviewsCountText: dailyrentData.rating?.reviewsCount || '',
  questionsCount: 0,
  questionsCountText: '0 вопросов',
  rating: extractNumber(dailyrentData.rating?.rating),
  ratingText: '',
  sections: dailyrentData.rating?.scores || [],
  seoData: {
    title: dailyrentData.seoData.headTitle,
    description: dailyrentData.seoData.description,
    location: '',
  },
  mlReview: null,
  builders: [],
  sellers: [],
});

export const mapDailyrentResponse = (
  dailyrentData: IPublicWebV1GetReviewsResponseSchema,
  offerRealtyId: number,
): TMappedDailyrentResponse => {
  const reviews: IExtendedReviewWebSchema[] = dailyrentData.reviews.map(mapDailyrentReview);
  const objectInfo = mapObjectInfo(dailyrentData, offerRealtyId);

  return {
    objectInfo,
    hasMore: false,
    reviews,
    moderationRulesUrl: dailyrentData.rating?.moderationRulesUrl || '',
    availableFilters: [],
    page: 0,
    tags: [],
    sort: [],
  };
};
